import { config } from "@/config";
import type { BlockStateEntry, BlockStateOp } from "@cohesive/ui-components";
import { BlockStateTokenRefused, blockStateTokens } from "./blockStateTokens";

/** One key as the block-state Worker sends it. */
export interface WireRow {
  key: string;
  value?: unknown;
  deleted?: true;
  version: number;
  updated_at?: string;
  created_by: string | null;
  updated_by: string | null;
}

export type WireOp =
  | { op: "set"; key: string; value: unknown; if_version?: number }
  | { op: "delete"; key: string; if_version?: number }
  | { op: "increment"; key: string; by?: number }
  | { op: "delete_prefix"; prefix: string };

export interface WireError {
  code: string;
  message: string;
  retry_after_ms?: number;
}

const REQUEST_TIMEOUT_MS = 10_000;

// Bare ids; names are filled in on the way out of the store.
export function fromWire(row: WireRow): BlockStateEntry {
  const author = (id: string | null | undefined) =>
    id ? { id, name: null, avatarUrl: null } : null;
  return {
    key: row.key,
    ...(row.deleted ? { deleted: true } : { value: row.value }),
    version: row.version,
    updatedAt: row.updated_at ?? null,
    createdBy: author(row.created_by),
    updatedBy: author(row.updated_by),
  };
}

export function toWire(op: BlockStateOp): WireOp {
  switch (op.op) {
    case "set":
      return {
        op: "set",
        key: op.key,
        value: op.value ?? null,
        if_version: op.ifVersion,
      };
    case "delete":
      return { op: "delete", key: op.key, if_version: op.ifVersion };
    case "increment":
      return { op: "increment", key: op.key, by: op.by };
    case "delete_prefix":
      return { op: "delete_prefix", prefix: op.prefix };
  }
}

/** The Worker could not be reached, or failed. */
export class BlockStateUnavailable extends Error {
  constructor(message = "Block data is unavailable.") {
    super(message);
    this.name = "BlockStateUnavailable";
  }
}

/** The Worker answered with an error. */
export class BlockStateRefused extends Error {
  constructor(
    readonly status: number,
    readonly error: WireError,
  ) {
    super(error.message);
    this.name = "BlockStateRefused";
  }
}

/** Never assumes the app's origin: production is cross-origin. */
export function blockStateUrl(path: string): URL {
  const base = config.hosts.blockState.replace(/\/$/, "");
  return new URL(base + path, window.location.href);
}

// The capture page's read; the app reads and writes through its hub.
async function call<T>(id: string, path: string, body: unknown): Promise<T> {
  for (let renewed = false; ; renewed = true) {
    const { token } = await blockStateTokens.get(id, renewed);
    let res: Response;
    try {
      res = await fetch(blockStateUrl(path), {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch {
      throw new BlockStateUnavailable();
    }
    // Refused before the object saw it, so resending cannot apply twice.
    if (res.status === 401 && !renewed) continue;
    if (res.status >= 500) throw new BlockStateUnavailable();
    const json = (await res.json().catch(() => null)) as
      | (T & { error?: WireError })
      | null;
    if (!json) throw new BlockStateUnavailable();
    if (!res.ok) {
      throw new BlockStateRefused(
        res.status,
        json.error ?? { code: "failed", message: res.statusText },
      );
    }
    return json;
  }
}

export function readBlockState(
  id: string,
): Promise<{ version: number; rows: WireRow[] }> {
  return call(id, "/v1/read", {});
}

/** Whether a failure means the data cannot be reached at all. */
export function isUnavailable(err: unknown): boolean {
  return (
    err instanceof BlockStateUnavailable ||
    err instanceof BlockStateTokenRefused ||
    !(err instanceof BlockStateRefused)
  );
}
